import React, { FunctionComponent, memo, ReactElement, RefObject, useCallback } from 'react'
import Grid from '@sport1/react-elements/Grid'
import Touchable from '@sport1/react-elements/Touchable'
import { TestID } from '../../utils/PropTypesDefinitions'
import { ImageIcon, ImageLabel } from '../../BaseComponents/Image/Components'
import { BigTeaserImage } from '../BigTeaser/Image/Components'
import Crawling from '../../Crawling'
import { AutoplayIcon } from './Components'

export interface StreamTeaserProps extends TestID {
    /** defines the label which is shown and the text color of the crawling text
     *
     * live: Label -> "Live" | TextColor -> pearl
     *
     * onyx: Label -> No Label | TextColor -> pearl
     *
     * breaking: Label -> "Breaking" | TextColor -> ORANGE
     * */
    labelType: 'live' | 'breaking'
    /** the height of the container in which the stream or the fallback image are shown */
    contentHeight: number
    /** the text of the crawl */
    headlineText?: string
    /** the fallback image if autoplay is false or no renderStream function was provided */
    mainImageUrl?: string
    /** boolean to determine if autoplay is on or off */
    autoplay: boolean
    /** function which should return the stream
     *
     * @TJS-type function
     * */
    renderStream?: () => ReactElement
    /** callback when the user pressed on the autoplay icon
     *
     * @TJS-type function
     * */
    onPressAutoPlay: (autoplay: boolean) => void
    /** callback when the user pressed on the stream teaser
     *
     * @TJS-type function
     * */
    onPress?: () => void
    /**
     * property for ref forwarding
     * @TJS-type React.RefObject<Element>
     * */
    innerRef?: RefObject<Element>
}

export const StreamContent: FunctionComponent<StreamTeaserProps> = ({
    labelType,
    contentHeight,
    headlineText,
    mainImageUrl,
    renderStream,
    autoplay,
    onPressAutoPlay,
    innerRef,
}) => {
    const [autoPlay, setAutoplay] = React.useState(autoplay)

    const onPressAutoplayIcon = useCallback((): void => {
        onPressAutoPlay(!autoPlay)
        setAutoplay(!autoPlay)
    }, [autoPlay, onPressAutoPlay])

    React.useEffect(() => {
        setAutoplay(autoplay)
    }, [autoplay])

    return (
        <>
            <Grid ref={innerRef} noExpand width="100%" height={contentHeight} position="relative">
                {renderStream && autoPlay ? (
                    renderStream()
                ) : (
                    <>
                        <BigTeaserImage imageUrl={mainImageUrl} isTopStory />
                        <ImageIcon type="video" />
                    </>
                )}
                <ImageLabel labels={[{ labelType }]} />
                <AutoplayIcon autoplay={autoPlay} onPress={onPressAutoplayIcon} />
            </Grid>
            {headlineText ? (
                <Crawling fontColor={labelType === 'breaking' ? 'ORANGE' : 'pearl'} spacer={50}>
                    {headlineText}
                </Crawling>
            ) : null}
        </>
    )
}

const StreamTeaser = ({
    labelType,
    contentHeight,
    headlineText,
    mainImageUrl,
    renderStream,
    autoplay,
    onPressAutoPlay,
    onPress,
    testID,
}: StreamTeaserProps): ReactElement => {
    return (
        <Touchable onPress={onPress} testID={testID}>
            <Grid noExpand flexShrink={0}>
                <StreamContent
                    labelType={labelType}
                    contentHeight={contentHeight}
                    autoplay={autoplay}
                    onPressAutoPlay={onPressAutoPlay}
                    headlineText={headlineText}
                    mainImageUrl={mainImageUrl}
                    renderStream={renderStream}
                />
            </Grid>
        </Touchable>
    )
}

export default memo(StreamTeaser)
